import compact from 'lodash/compact';
import dayjs from '~/timeInit';

import { ORDER_STATUS_COMPLETED } from '~/consts/order';

import { toDayJs } from '~/services/TimeService';

import { getParentFlightSources } from './parentFlightSources';

/** A completed order holding a flight the customer can still raise a post-purchase offer against. */
export interface ParentFlightOrder {
  order: App.Order;
  /** The item the offer is raised against: the first active flight or flight custom-offer item. */
  parentItemId: string;
  isCustomOffer: boolean;
  pnrs: Array<string>;
  departureDate: Date;
  lastTravelDate: Date;
}

function toTimes(dates: Array<string | undefined>): Array<number> {
  return compact(dates)
    .map(toDayJs)
    .filter((date) => date.isValid())
    .map((date) => date.valueOf());
}

function toParentFlightOrder(order: App.Order): ParentFlightOrder | undefined {
  if (order.status !== ORDER_STATUS_COMPLETED) {
    return undefined;
  }

  const sources = getParentFlightSources(order);
  const flights = sources.flatMap((source) => source.flights);
  const departureTimes = toTimes(flights.map((flight) => flight.departingDate));
  const travelTimes = toTimes(flights.map((flight) => flight.arrivalDate ?? flight.departingDate));

  if (!departureTimes.length) {
    return undefined;
  }

  const [primarySource] = sources;

  return {
    order,
    parentItemId: primarySource.id,
    isCustomOffer: primarySource.isCustomOffer,
    pnrs: compact(sources.map((source) => source.pnr?.trim())),
    departureDate: new Date(Math.min(...departureTimes)),
    lastTravelDate: new Date(Math.max(...travelTimes)),
  };
}

/**
 * Completed orders with an active flight that has not fully flown, soonest departure first.
 * svc-order only checks completed, customer and brand; the "not yet flown" rule is ours.
 */
export function getEligibleParentFlightOrders(orders: Array<App.Order>, today = new Date()): Array<ParentFlightOrder> {
  const startOfToday = dayjs(today).startOf('day');

  return compact(orders.map(toParentFlightOrder))
    .filter((parent) => !dayjs(parent.lastTravelDate).isBefore(startOfToday))
    .sort((left, right) => left.departureDate.getTime() - right.departureDate.getTime());
}

export function getParentFlightOrderLabel(parent: ParentFlightOrder): string {
  const orderNumber = parent.isCustomOffer ? `${parent.order.number} (Custom offer)` : parent.order.number;

  return compact([orderNumber, parent.pnrs.length ? `PNR ${parent.pnrs.join(', ')}` : undefined]).join(' · ');
}
